<template>
  <v-card class="mx-auto my-0 py-0 card-style">
    <v-alert v-if="alert" class="mt-4" :type="typeAlert">{{ error }}</v-alert>
    <v-layout>
      <v-app-bar color="white">
        <template #image>
          <v-img color="white"/>
        </template>
        <template #prepend>
          <v-app-bar-nav-icon color="primary" @click="emits('toggle-drawer')"/>
        </template>
        <v-app-bar-title class="clinicbox-title">
          <router-link class="" to="/home">
            <v-img src="@/assets/img/logo_color.png" width="130"/>
          </router-link>
        </v-app-bar-title>
        <v-spacer/>
        <span v-if="userName" class="clinicbox-title mr-5 text-secondary">{{ userName }}</span>
        <v-tooltip bottom>
          <template #activator="{ props }">
            <v-btn fab dark color="primary" :to="{ name: 'UserProfile' }" class="appbar-style mx-1" plain
                   v-bind="props">
              <v-icon icon="mdi-account-circle"/>
            </v-btn>
          </template>
          <span>Perfil de Usuario</span>
        </v-tooltip>
        <v-tooltip bottom v-if="uploadEnable && hasReadAllDocuments">
          <template #activator="{ props }">
            <v-btn fab dark color="primary" class="appbar-style mx-1" :to="{ name: 'UploadFiles' }" plain
                   v-bind="props">
              <v-icon icon="mdi-upload"/>
            </v-btn>
          </template>
          <span>Subir Archivo</span>
        </v-tooltip>
        <v-tooltip bottom v-if="route.name !== 'DocumentClassification' && uploadEnable && hasReadAllDocuments">
          <template #activator="{ props }">
            <v-btn fab dark color="primary" id="classify-file-app-header-btn" v-bind="props"
                   class="appbar-style" :to="{ name: 'DocumentClassification' }" plain>
              <v-icon icon="mdi-file-document-arrow-right-outline"/>
            </v-btn>
          </template>
          <span>Classify</span>
        </v-tooltip>
        <v-tooltip bottom v-if="hasReadAllDocuments">
          <template #activator="{ props }">
            <v-btn fab dark color="primary" :to="{ name: 'Inbox' }" plain class="appbar-style mx-1" v-bind="props">
              <v-badge :color="inboxNotifications > 0 ? 'red accent-5' : 'transparent'"
                       :content="inboxNotifications !== 0 ? inboxNotifications : ''">
                <v-icon icon="mdi-email"/>
              </v-badge>
            </v-btn>
          </template>
          <span>Notificaciones</span>
        </v-tooltip>
        <v-tooltip bottom>
          <template #activator="{ props }">
            <v-btn fab dark color="primary" @click="dialogFeedback=true" v-bind="props">
              <v-icon icon="mdi-send-circle"/>
            </v-btn>
          </template>
          <span>Feedback</span>
        </v-tooltip>
        <v-tooltip bottom>
          <template #activator="{ props }">
            <v-btn fab dark color="primary" @click="logout" v-bind="props">
              <v-icon icon="mdi-logout"/>
            </v-btn>
          </template>
          <span>Cerrar Sesión</span>
        </v-tooltip>
      </v-app-bar>
      <v-main/>
    </v-layout>
    <v-dialog v-model="dialogFeedback" max-width="900px">
      <v-card>
        <v-card-title><span class="text-h5 text-primary">Send a feedback</span></v-card-title>
        <v-card-text>
          <v-container fluid>
            <v-row>
              <v-col cols="12">
                <v-text-field
                  v-model="userEmail" readonly required hide-details label="From*" class="no-border"/>
              </v-col>
              <v-col cols="12" class="pb-0">
                <v-text-field
                  v-model="feedback" clearable required label="Description*" class="lg-h"
                  maxlength="1000" counter
                  :color="feedback.length >= 1000 ? 'error' : undefined"/>
              </v-col>
              <v-col cols="12">
                <v-file-input
                  id="file"
                  ref="file"
                  name="fileToUpload"
                  prepend-icon="mdi-paperclip"
                  enctype="multipart/form-data"
                  label="Attachment"
                  @change="checkFile($event)"
                  @click:clear="onFileClear"
                  :accept="Constants.ACCEPTED_FILE_EXTENSIONS_FEEDBACK.join(', ')">
                  <template #selection>
                    <v-chip v-for="file in selectedFiles" :key="file.name" dark label small>{{ file.name }}</v-chip>
                  </template>
                </v-file-input>
                <div>
                  <AlertMessage
                    :colorTextClass="!uploadButtonFail ? 'black' : 'text-red'"
                    :colorIcon="!uploadButtonFail ? 'blue' : 'red'"
                    :icon="'info'"
                    :message="acceptedFileExtensionInfoMessage"/>
                </div>
              </v-col>
            </v-row>
          </v-container>
        </v-card-text>
        <v-card-actions>
          <v-spacer/>
          <v-btn @click="dialogFeedback=false" color="error">Cancel</v-btn>
          <v-btn :disabled="!canSendFeedback" @click="sendEmailFeedback" color="primary">Send</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </v-card>
</template>
<script setup>
  import { ref, computed, onMounted } from 'vue';
  import { useRoute, useRouter } from 'vue-router';
  import { useStore } from 'vuex';
  import { createCoreServices } from '@/services/coreService';
  import { createAuthServices } from '@/services/authServices';
  import { createUserServices } from '@/services/userService';
  import { createNotificationServices } from '@/services/notificationServices';
  import { roleManager } from '@/store/roleManager';
  import cookieManager from '@/utils/cookieManager';
  import { Constants } from '@/store/constants';
  import AlertMessage from '@/components/common/AlertMessage.vue';

  const emits = defineEmits(['toggle-drawer']);

  const router = useRouter();
  const route = useRoute();
  const store = useStore();
  const coreServices = createCoreServices();
  const authServices = createAuthServices(coreServices);
  const notificationServices = createNotificationServices(coreServices);
  const userServices = createUserServices(coreServices);

  const error = ref('');
  const typeAlert = ref('error');
  const alert = ref(false);
  const loading = ref(false);
  const uploadEnable = ref(false);
  const inboxNotifications = ref(0);
  const dialogFeedback = ref(false);
  const feedback = ref('');
  const uploadButtonFail = ref(false);
  const selectedFiles = ref([]);
  const base64Files = ref([]);
  const extensions = ref([]);
  const urls = ref([]);

  const user = computed(() => store.getters.user);

  const acceptedFileExtensionInfoMessage = computed(() => {
    const exts = Constants.ACCEPTED_FILE_EXTENSIONS_FEEDBACK;
    return `Only files with extension: ${exts.slice(0, -1).join(', ')} or ${exts.slice(-1)}`;
  });
  const canSendFeedback = computed(() => feedback.value !== '' && feedback.value && !uploadButtonFail.value);
  const userName = computed(() => (user.value ? `${user.value.first_name}` : ''));
  const userEmail = computed(() => (user.value ? `${user.value.email}` : ''));
  const hasReadAllDocuments = computed(() => (user.value ? user.value.has_read_all_app_documents : false));

  const logout = () => {
    authServices.logout()
      .finally(() => {
        cookieManager.deleteAll();
        router.push({ name: 'Start' });
      });
  };

  const fileToBase64 = (file) => new Promise((resolve) => {
    const reader = new FileReader();
    reader.onload = () => {
      const base64 = reader.result.split(',')[1];
      resolve(base64);
    };
    reader.readAsDataURL(file);
  });

  const handleFile = async (file) => {
    const ext = file.name.split('.').pop().toLowerCase();
    const base64 = await fileToBase64(file);
    if (ext === 'pdf') {
      base64Files.value.push(base64);
    } else {
      urls.value.push(base64);
    }
  };

  const handleFiles = async () => {
    const promises = selectedFiles.value.map((file) => handleFile(file));
    await Promise.all(promises);
  };

  const validateFiles = () => {
    let fail = false;
    urls.value = [];
    base64Files.value = [];
    extensions.value = [];
    uploadButtonFail.value = selectedFiles.value.some((file) => {
      const ext = file.name.split('.').pop();
      if (!Constants.ACCEPTED_FILE_EXTENSIONS_FEEDBACK.includes(`.${ext}`)) {
        fail = true;
        return true;
      }
      extensions.value.push(ext);
      return false;
    });
    if (!fail) {
      handleFiles();
    }
  };

  const restartFiles = () => {
    extensions.value = [];
    urls.value = [];
    base64Files.value = [];
    selectedFiles.value = [];
  };

  const onFileClear = () => {
    selectedFiles.value = [];
    uploadButtonFail.value = false;
    restartFiles();
  };

  const checkFile = async (event) => {
    selectedFiles.value = [...event.target.files];
    validateFiles();
  };

  const sendEmailFeedback = () => {
    loading.value = true;
    dialogFeedback.value = false;
    const archive = document.querySelector('#file');
    userServices.sendEmailFeedback(archive.files[0], feedback.value)
      .then(() => {
        loading.value = false;
        feedback.value = '';
        typeAlert.value = 'success';
        error.value = 'Email sent successfully';
        alert.value = true;
        setTimeout(() => {
          alert.value = false;
        }, 3000);
      })
      .catch(() => {
        loading.value = false;
        typeAlert.value = 'error';
        alert.value = true;
        error.value = 'error on sending email';
        setTimeout(() => {
          alert.value = false;
        }, 3000);
      });
  };

  onMounted(async () => {
    uploadEnable.value = roleManager.isUploader();
    try {
      if (cookieManager.hasReadAllAppDocuments()) {
        const nc = await notificationServices.getNotificationCount();
        inboxNotifications.value = nc.data['count'];
      }
    } catch {
      console.log();
    }
  });
</script>
<style scoped>
  .card-style {
      border-radius: 0;
      margin-top: 0 !important;
  }
  .registered-user {
    pointer-events: none;
  }
  .v-card__subtitle {
    padding: 0;
  }
  .subtitle-description {
    padding-bottom: 16px !important;
  }
  .v-alert {
    position: fixed;
    right: 10px;
    top: 10px;
    color: white;

  }
</style>
